<template>
    <div class="block-editor zoho-create-record">
        <div class="wrapper">
            <section>
                <div class="title">{{ $t("message.zohoModuleName") }}e</div>
                <CustomPicker
                    v-model="selectedModule"
                    :items="modules"
                    idKey="api_name"
                    labelKey="plural_label"
                    :title="$t('message.selectModule')"
                />
            </section>

            <section>
                <div class="title">{{ $t("message.standardFields") }}</div>
                <div class="field-picker-wrapper">
                    <CustomPicker
                        v-model="selectedStandardField"
                        :items="availableStandardFields"
                        idKey="api_name"
                        labelKey="field_label"
                        title="Select Standard Field"
                    />
                    <SupsisButton @click="addField(selectedStandardField)">
                        <i class="material-icons">add</i>
                        <span>Add Field</span>
                    </SupsisButton>
                </div>
            </section>

            <section>
                <div class="title">{{ $t("message.customFields") }}</div>
                <div class="field-picker-wrapper">
                    <CustomPicker
                        v-model="selectedCustomField"
                        :items="availableCustomFields"
                        idKey="api_name"
                        labelKey="field_label"
                        :title="$t('message.selectCustomFields')"
                    />
                    <SupsisButton @click="addField(selectedCustomField)">
                        <i class="material-icons">add</i>
                        <span>Add Field</span>
                    </SupsisButton>
                </div>
            </section>

            <section v-for="(field, fieldApiName) in clone.fields" :key="fieldApiName">
                <div class="title">{{ field.fieldMeta.field_label }}</div>
                <div class="text-editor-wrapper" v-if="field.fieldMeta.data_type !== 'picklist'">
                    <TiptapEditor
                        :placeholder="field.fieldMeta.field_label"
                        :show-attributes="true"
                        :show-emoji="false"
                        v-model="field.value"
                    />
                </div>
                <CustomPicker
                    v-else
                    v-model="field.value"
                    :items="field.fieldMeta.pick_list_values"
                    idKey="actual_value"
                    labelKey="display_value"
                    :title="field.fieldMeta.field_label"
                />
            </section>
        </div>
    </div>
</template>

<script>
import log2 from "../../../../../../../utils/log2";
import _ from "lodash";
import TiptapEditor from "../../../../../Chatbot/pages/dashboard/common/TiptapEditor.vue";
import CustomPicker from "../../../../../../../components/pickers/CustomPicker.vue";
import SupsisButton from "../../../../../Settings/components/SupsisButton.vue";
import { ZOHO_API } from "../../../../../../../api/zoho";

const log = log2("ZohoCreateRecord");
export default {
    name: "ZohoCreateRecord",
    components: { TiptapEditor, CustomPicker, SupsisButton },
    props: ["value"],
    data() {
        return {
            modules: [],
            selectedModule: null,
            clone: _.cloneDeep(this.value) || {
                module: "",
                fields: {},
            },
            selectedStandardField: null,
            selectedCustomField: null,
            allFields: [],
        };
    },
    computed: {
        standardFields() {
            return Object.values(this.clone.fields || {}).filter((field) => !field.fieldMeta.custom_field);
        },
        customFields() {
            return Object.values(this.clone.fields || {}).filter((field) => field.fieldMeta.custom_field);
        },
        availableStandardFields() {
            const addedFields = Object.keys(this.clone.fields || {});
            return this.allFields.filter((field) => !field.custom_field && !addedFields.includes(field.api_name));
        },
        availableCustomFields() {
            const addedFields = Object.keys(this.clone.fields || {});
            return this.allFields.filter((field) => field.custom_field && !addedFields.includes(field.api_name));
        },
    },

    methods: {
        async fetchModules() {
            log("fetchModules");
            const modules = await ZOHO_API.listModules();
            this.modules = modules;
        },
        async onModuleChange(module) {
            log("onModuleChange", module);
            this.selectedModule = module;
            if (module !== this.clone.module) {
                this.clone.module = module;
                this.clone.fields = {};
                this.selectedStandardField = null;
                this.selectedCustomField = null;
            }

            const fields = await ZOHO_API.listModuleFields(module);
            this.allFields = fields;
            if (module !== this.clone.module) {
                fields.forEach((field) => {
                    if (field.system_mandatory) {
                        this.clone.fields[field.api_name] = {
                            fieldApiName: field.api_name,
                            value: null,
                            fieldMeta: field,
                        };
                    }
                });
            }
        },
        addField(fieldApiName) {
            log("addField", fieldApiName);
            if (fieldApiName) {
                const fieldMeta = this.allFields.find((f) => f.api_name === fieldApiName);
                if (this.clone.fields[fieldApiName]) {
                    return;
                }
                this.clone.fields[fieldApiName] = {
                    fieldApiName: fieldApiName,
                    value: null,
                    fieldMeta: fieldMeta,
                };
                this.selectedStandardField = null;
                this.selectedCustomField = null;
            }
        },
    },
    watch: {
        clone: {
            deep: true,
            handler(newValue) {
                log("clone changed", newValue);
                this.$emit("input", newValue);
            },
        },
        selectedModule: {
            handler(newValue) {
                log("selectedModule changed", newValue);
                this.onModuleChange(newValue);
            },
        },
    },
    created() {
        this.fetchModules();
        if (this.value.module) {
            this.onModuleChange(this.value.module);
        }
    },
};
</script>

<style lang="scss">
.block-editor.zoho-create-record {
    section {
        margin-bottom: 10px;
    }

    .wrapper {
        padding: 30px;

        .title {
            margin-bottom: 10px;
            font-weight: 600;
            font-size: 16px;
            line-height: 20px;
        }

        .text-editor-wrapper {
            border-radius: 0px;
            border: 1px solid #fff;
            background: white;

            &:hover {
                border: 1px solid #006cff !important;
            }
        }
    }

    .field-picker-wrapper {
        display: flex;
        align-items: center;
        gap: 10px;
    }
}
</style>
